/**
 * Kleiner localStorage-Store für "zuletzt besuchte" Elemente, die in der
 * Command-Palette als Default-Empty-State gezeigt werden.
 *
 * Nicht multi-tab-safe (BroadcastChannel wäre Overkill), nicht encrypted.
 * Nur Metadaten: id/type/label/href — nichts Geheimes.
 */

export type RecentItemType = "invoice" | "supplier" | "workflow" | "payment-run";

export interface RecentItem {
  id: string;
  type: RecentItemType;
  label: string;
  sub?: string;
  href: string;
  visitedAt: number;
}

const KEY = "paypilot:recent-items";
const MAX = 20;

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

export function getRecentItems(): RecentItem[] {
  if (!isBrowser()) return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as RecentItem[];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x) => x && typeof x.id === "string" && typeof x.href === "string")
      .sort((a, b) => b.visitedAt - a.visitedAt);
  } catch {
    return [];
  }
}

export function pushRecentItem(item: Omit<RecentItem, "visitedAt">): void {
  if (!isBrowser()) return;
  try {
    const existing = getRecentItems();
    const filtered = existing.filter((x) => !(x.id === item.id && x.type === item.type));
    const next: RecentItem[] = [{ ...item, visitedAt: Date.now() }, ...filtered].slice(0, MAX);
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* ignore quota errors */
  }
}

export function clearRecentItems(): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
